<template>
  <div class="projection-table-root">
    <!-- Notice when in default 2-week view and there are more weeks -->
    <div
      v-if="
        selectedFilter === 'default' &&
        totalRegisteredCount > weekGroups.length &&
        weekGroups.length > 0
      "
      class="projection-filter-notice"
    >
      <div class="d-flex align-center">
        <v-icon small color="#3b5bdb" class="mr-2">mdi-eye-outline</v-icon>
        <span>
          Vista predeterminada: Mostrando <strong>{{ weekGroups.length }}</strong> de
          <strong>{{ totalRegisteredCount }}</strong> semanas registradas (1 inactiva + 1 activa).
        </span>
      </div>
      <v-btn
        x-small
        text
        color="primary"
        class="font-weight-bold"
        @click="$emit('show-all')"
      >
        <v-icon x-small class="mr-1">mdi-format-list-bulleted</v-icon>
        Ver todas
      </v-btn>
    </div>

    <!-- Empty state -->
    <div v-if="weekGroups.length === 0" class="projection-empty-state">
      <v-icon size="48" color="#cbd5e1">mdi-calendar-blank-multiple</v-icon>
      <span class="empty-title">Sin proyecciones registradas</span>
      <span class="empty-hint">Haz clic en "Agregar Semana" para comenzar</span>
    </div>

    <!-- Accordion by week -->
    <v-expansion-panels
      v-else
      v-model="expandedPanels"
      multiple
      flat
      class="week-panels"
    >
      <v-expansion-panel
        v-for="week in weekGroups"
        :key="week.PdsID"
        class="week-panel"
        :class="{ 'week-panel--locked': week.isLocked }"
      >
        <!-- Panel Header -->
        <v-expansion-panel-header class="week-panel-header" disable-icon-rotate>
          <div class="week-header-content">
            <div class="week-header-left">
              <div class="week-name-row">
                <v-icon
                  small
                  class="mr-2"
                  :color="week.isLocked ? '#ef4444' : '#10b981'"
                >
                  {{ week.isLocked ? "mdi-lock" : "mdi-calendar-check" }}
                </v-icon>
                <span class="week-name">{{ week.weekName }}</span>
                <span class="week-dates-separator mx-1 text--secondary">|</span>
                <div class="week-dates">
                  {{ $fun.formatDateView(week.PdsDateBegin) }} -
                  {{ $fun.formatDateView(week.PdsDateEnd) }}
                </div>
                <v-chip
                  x-small
                  :color="week.isLocked ? '#ef4444' : '#10b981'"
                  dark
                  class="ml-2 week-status-chip"
                >
                  <v-icon x-small left class="mr-1">
                    {{ week.isLocked ? "mdi-lock-outline" : "mdi-lock-open-variant-outline" }}
                  </v-icon>
                  {{ week.isLocked ? "Inactiva (Congelada)" : "Activa (Abierta)" }}
                </v-chip>
              </div>
              <div class="week-summary-badges">
                <span
                  v-for="turn in week.turns"
                  :key="turn.TypeTurn"
                  class="turn-badge"
                >
                  <v-icon x-small class="mr-1" color="#64748b">
                    {{
                      turn.TypeTurn == 1
                        ? "mdi-weather-sunny"
                        : turn.TypeTurn == 2
                        ? "mdi-weather-night"
                        : "mdi-swap-horizontal"
                    }}
                  </v-icon>
                  {{ getTurnName(turn.TypeTurn) }}:
                  {{ turn.WprQuantityProject }}
                </span>
              </div>
            </div>
            <div class="week-header-right">
              <div class="week-total-pill">
                <span class="total-label">Total</span>
                <span
                  class="total-value"
                  :class="{
                    'total-over': week.totalQty > reqQuantity,
                    'total-ok': week.totalQty <= reqQuantity,
                  }"
                >
                  {{ week.totalQty }}
                </span>
              </div>
            </div>
          </div>
          <template v-slot:actions>
            <v-icon color="#94a3b8">mdi-chevron-down</v-icon>
          </template>
        </v-expansion-panel-header>

        <!-- Panel Content -->
        <v-expansion-panel-content class="week-panel-body">
          <table class="turns-detail-table">
            <thead>
              <tr>
                <th class="text-left">Turno</th>
                <th class="text-center">Hombres</th>
                <th class="text-center">Mujeres</th>
                <th class="text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="turn in week.turns" :key="turn.WprID || turn.TypeTurn">
                <td>
                  <div class="turn-name-cell">
                    <v-icon
                      x-small
                      class="mr-1"
                      :color="
                        turn.TypeTurn == 1
                          ? '#f59e0b'
                          : turn.TypeTurn == 2
                          ? '#6366f1'
                          : '#10b981'
                      "
                    >
                      {{
                        turn.TypeTurn == 1
                          ? "mdi-weather-sunny"
                          : turn.TypeTurn == 2
                          ? "mdi-weather-night"
                          : "mdi-swap-horizontal"
                      }}
                    </v-icon>
                    {{ getTurnName(turn.TypeTurn) }}
                  </div>
                </td>
                <td class="text-center">
                  <span class="qty-cell">{{ turn.WprQuantityMale }}</span>
                </td>
                <td class="text-center">
                  <span class="qty-cell">{{ turn.WprQuantityFemale }}</span>
                </td>
                <td class="text-center">
                  <span class="qty-cell qty-cell--total">{{
                    turn.WprQuantityProject
                  }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td class="font-weight-bold">Total Semana</td>
                <td class="text-center font-weight-bold">
                  {{ week.totalMale }}
                </td>
                <td class="text-center font-weight-bold">
                  {{ week.totalFemale }}
                </td>
                <td class="text-center">
                  <span
                    class="qty-cell qty-cell--grand"
                    :class="{ 'qty-over': week.totalQty > reqQuantity }"
                  >
                    {{ week.totalQty }}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>

          <!-- Edit week button -->
          <div class="week-actions">
            <v-btn
              small
              color="primary"
              class="edit-week-btn"
              :disabled="week.isLocked"
              @click.stop="$emit('edit-week', week)"
            >
              <v-icon small class="mr-1">mdi-pencil-outline</v-icon>
              Editar Semana
            </v-btn>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Global totals bar -->
    <div v-if="weekGroups.length > 0" class="global-totals-bar">
      <div class="global-totals-left">
        <v-icon small color="#475569" class="mr-1">mdi-sigma</v-icon>
        <span class="global-label">Resumen General</span>
      </div>
      <div class="global-totals-right">
        <span class="global-stat">
          <span class="stat-label">Semanas:</span>
          <span class="stat-value">{{ weekGroups.length }}</span>
        </span>
        <span class="global-stat">
          <span class="stat-label">Vacantes/semana:</span>
          <span class="stat-value">{{ reqQuantity }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProjectionTable",
  props: {
    weekGroups: {
      type: Array,
      default: () => [],
    },
    totalRegisteredCount: {
      type: Number,
      default: 0,
    },
    reqQuantity: {
      type: Number,
      default: 0,
    },
    selectedFilter: {
      type: [String, Number],
      default: "default",
    },
  },
  data() {
    return {
      expandedPanels: [],
    };
  },
  watch: {
    weekGroups: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.expandedPanels = val.map((_, idx) => idx);
        } else {
          this.expandedPanels = [];
        }
      },
    },
  },
  methods: {
    getTurnName(typeTurn) {
      const t = Number(typeTurn);
      if (t === 1) return "Día";
      if (t === 2) return "Noche";
      return "Mixto";
    },
  },
};
</script>

<style scoped>
/* ── Root ────────────────────────────── */
.projection-table-root {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Filter Notice ───────────────────── */
.projection-filter-notice {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #1e40af;
}

.week-dates {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}

/* ── Empty State ─────────────────────── */
.projection-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%);
}

.empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #475569;
}

.empty-hint {
  font-size: 12px;
  color: #94a3b8;
}

/* ── Week Panels ─────────────────────── */
.week-panels {
  border-radius: 10px;
  overflow: hidden;
}

.week-panel {
  border: 1px solid #e2e8f0;
  border-radius: 10px !important;
  margin-bottom: 10px;
  transition: box-shadow 0.2s, border-color 0.2s;
  overflow: hidden;
}

.week-panel:hover {
  box-shadow: 0 4px 16px rgba(59, 91, 219, 0.08);
  border-color: #c7d2fe;
}

.week-panel--locked {
  background: #fef2f2;
  border-color: #fecaca;
}

.week-panel--locked:hover {
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.06);
  border-color: #fca5a5;
}

/* ── Panel Header ────────────────────── */
.week-panel-header {
  padding: 14px 18px !important;
  min-height: auto !important;
}

.week-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-right: 12px;
}

.week-header-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.week-name-row {
  display: flex;
  align-items: center;
}

.week-name {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.week-status-chip {
  font-size: 10px !important;
  height: 20px !important;
  letter-spacing: 0.04em;
}

.week-summary-badges {
  display: flex;
  gap: 14px;
  padding-left: 28px;
}

.turn-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* ── Total Pill ──────────────────────── */
.week-total-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 14px;
  min-width: 64px;
}

.total-label {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
}

.total-value {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
}

.total-ok {
  color: #10b981;
}

.total-over {
  color: #ef4444;
}

/* ── Panel Body ──────────────────────── */
.week-panel-body {
  padding: 0 !important;
}

.week-panel-body >>> .v-expansion-panel-content__wrap {
  padding: 0 18px 16px;
}

/* ── Detail Table ────────────────────── */
.turns-detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 4px;
}

.turns-detail-table thead tr {
  border-bottom: 2px solid #e2e8f0;
}

.turns-detail-table th {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  padding: 8px 12px;
}

.turns-detail-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}

.turns-detail-table tbody tr:hover {
  background: #f8fafc;
}

.turns-detail-table td {
  padding: 10px 12px;
  color: #334155;
}

.turn-name-cell {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.qty-cell {
  font-weight: 500;
  color: #475569;
}

.qty-cell--total {
  font-weight: 700;
  color: #1e293b;
}

.qty-cell--grand {
  font-size: 14px;
  font-weight: 800;
  color: #10b981;
}

.qty-over {
  color: #ef4444 !important;
}

/* ── Totals Row ──────────────────────── */
.totals-row {
  background: #f8fafc;
  border-top: 2px solid #e2e8f0 !important;
}

.totals-row td {
  color: #1e293b;
  font-weight: 600;
}

/* ── Week Actions ────────────────────── */
.week-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  margin-top: 8px;
}

.edit-week-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.01em;
  border-radius: 6px;
}

/* ── Global Totals Bar ───────────────── */
.global-totals-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-top: 2px;
}

.global-totals-left {
  display: flex;
  align-items: center;
}

.global-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.global-totals-right {
  display: flex;
  gap: 20px;
}

.global-stat {
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}

.stat-value {
  font-size: 13px;
  font-weight: 700;
  color: #3b5bdb;
}
</style>
